import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Check, GraduationCap, Building2, Users, Upload, CalendarDays, PartyPopper, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ImportStudentsDialog } from "@/components/ImportStudentsDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  { icon: GraduationCap, label: "Compte" },
  { icon: Building2, label: "École" },
  { icon: Users, label: "Classes" },
  { icon: Upload, label: "Élèves" },
  { icon: CalendarDays, label: "Calendrier" },
  { icon: PartyPopper, label: "Fin" },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(user ? 2 : 1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      api.get("/calendar/options")
        .then(({ data }) => setCal((c) => ({ ...c, options: data, academie: data.academies[0] || "", year: data.years[0] || "" })))
        .catch(() => {});
    }
  }, [user]);

  const [account, setAccount] = useState({ identifiant: "", password: "", first_name: "", last_name: "" });
  const [school, setSchool] = useState({ name: "", city: "", postal_code: "", address: "" });
  const [classes, setClasses] = useState([]);
  const [newClass, setNewClass] = useState({ name: "", level: "" });
  const [importFor, setImportFor] = useState(null);
  const [cal, setCal] = useState({ options: { academies: [], years: [] }, academie: "", year: "" });

  const upd = (setter) => (k) => (e) => setter((s) => ({ ...s, [k]: e.target.value }));

  const submitAccount = async () => {
    setBusy(true);
    try {
      await api.post("/auth/register-teacher", account);
      await refresh();
      const { data } = await api.get("/calendar/options");
      setCal((c) => ({ ...c, options: data, academie: data.academies[0] || "", year: data.years[0] || "" }));
      toast.success("Compte enseignant créé.");
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitSchool = async () => {
    setBusy(true);
    try {
      await api.put("/school", school);
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const addClass = async () => {
    if (!newClass.name.trim()) { toast.error("Le nom de la classe est obligatoire."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/classes", newClass);
      setClasses((c) => [...c, { id: data.id, name: data.name, level: data.level }]);
      setNewClass({ name: "", level: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const importCalendar = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/calendar/import", { params: { year: cal.year, academie: cal.academie } });
      await api.put("/calendar", { periods: data.periods.map((p) => ({ index: p.index, start: p.start, end: p.end })) });
      toast.success("Calendrier officiel importé et enregistré.");
      setStep(6);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] dark:bg-[#0A0A0A]">
      <div className="mx-auto flex max-w-xl flex-col px-4 py-10">
        <div className="mb-8 flex items-center gap-2.5">
          <Logo size={36} />
          <span className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">Le Passeport</span>
        </div>

        {/* Progression */}
        <div className="mb-8 flex items-center justify-between" data-testid="wizard-progress">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <div key={s.label} className="flex flex-1 flex-col items-center gap-1.5">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                  done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-slate-100 text-slate-400 dark:bg-white/10"
                }`}>
                  {done ? <Check size={16} /> : n}
                </div>
                <span className={`hidden text-[11px] sm:block ${active ? "text-slate-700 dark:text-slate-200" : "text-slate-400"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)] dark:border-white/10 dark:bg-white/[0.03]"
          data-testid={`wizard-step-${step}`}
        >
          {step === 1 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Créez votre compte enseignant</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Bienvenue ! Configurons votre établissement en quelques étapes.</p>
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5"><Label>Prénom</Label><Input value={account.first_name} onChange={upd(setAccount)("first_name")} data-testid="wizard-first-name" /></div>
                  <div className="flex flex-col gap-1.5"><Label>Nom</Label><Input value={account.last_name} onChange={upd(setAccount)("last_name")} data-testid="wizard-last-name" /></div>
                </div>
                <div className="flex flex-col gap-1.5"><Label>Identifiant</Label><Input value={account.identifiant} onChange={upd(setAccount)("identifiant")} placeholder="ex : prenom.nom" data-testid="wizard-identifiant" /></div>
                <div className="flex flex-col gap-1.5"><Label>Mot de passe</Label><Input type="password" value={account.password} onChange={upd(setAccount)("password")} data-testid="wizard-password" /></div>
              </div>
              <Button onClick={submitAccount} disabled={busy} className="mt-5 w-full rounded-xl" data-testid="wizard-account-next">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <>Continuer <ArrowRight size={16} className="ml-2" /></>}
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Votre établissement</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Ces informations apparaîtront sur vos documents.</p>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5"><Label>Nom de l'établissement</Label><Input value={school.name} onChange={upd(setSchool)("name")} data-testid="wizard-school-name" /></div>
                <div className="flex flex-col gap-1.5"><Label>Adresse</Label><Input value={school.address} onChange={upd(setSchool)("address")} data-testid="wizard-school-address" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5"><Label>Code postal</Label><Input value={school.postal_code} onChange={upd(setSchool)("postal_code")} data-testid="wizard-school-postal" /></div>
                  <div className="flex flex-col gap-1.5"><Label>Ville</Label><Input value={school.city} onChange={upd(setSchool)("city")} data-testid="wizard-school-city" /></div>
                </div>
              </div>
              <Button onClick={submitSchool} disabled={busy} className="mt-5 w-full rounded-xl" data-testid="wizard-school-next">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <>Continuer <ArrowRight size={16} className="ml-2" /></>}
              </Button>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Vos classes</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Ajoutez une ou plusieurs classes.</p>
              <div className="flex items-end gap-2">
                <div className="flex flex-1 flex-col gap-1.5"><Label>Nom</Label><Input value={newClass.name} onChange={upd(setNewClass)("name")} placeholder="ex : CE2 A" data-testid="wizard-class-name" /></div>
                <div className="flex w-24 flex-col gap-1.5"><Label>Niveau</Label><Input value={newClass.level} onChange={upd(setNewClass)("level")} placeholder="CE2" data-testid="wizard-class-level" /></div>
                <Button onClick={addClass} disabled={busy} variant="outline" className="rounded-xl" data-testid="wizard-add-class"><Plus size={16} /></Button>
              </div>
              {classes.length > 0 && (
                <div className="mt-4 flex flex-col gap-2" data-testid="wizard-classes-list">
                  {classes.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 text-sm dark:border-white/10">
                      <span className="font-medium text-slate-700 dark:text-slate-200">{c.name}{c.level ? ` · ${c.level}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={() => setStep(4)} disabled={classes.length === 0} className="mt-5 w-full rounded-xl" data-testid="wizard-classes-next">
                Continuer <ArrowRight size={16} className="ml-2" />
              </Button>
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Importez vos élèves</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Importez une liste (.xlsx / .csv) pour chaque classe, ou faites-le plus tard.</p>
              <div className="flex flex-col gap-2">
                {classes.map((c) => (
                  <button key={c.id} onClick={() => setImportFor(c.id)}
                    className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-left text-sm transition-transform hover:scale-[0.99] dark:border-white/10"
                    data-testid={`wizard-import-${c.id}`}>
                    <span className="font-medium text-slate-700 dark:text-slate-200">{c.name}</span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><Upload size={14} /> Importer</span>
                  </button>
                ))}
              </div>
              <Button onClick={() => setStep(5)} className="mt-5 w-full rounded-xl" data-testid="wizard-students-next">
                Continuer <ArrowRight size={16} className="ml-2" />
              </Button>
              <ImportStudentsDialog
                open={!!importFor} onOpenChange={(v) => !v && setImportFor(null)}
                classId={importFor} onImported={() => {}} onRequestPrint={() => {}}
              />
            </>
          )}

          {step === 5 && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Calendrier scolaire</h1>
              <p className="mt-1 mb-5 text-sm text-slate-500">Importez les périodes officielles de votre académie.</p>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Académie</Label>
                  <select value={cal.academie} onChange={(e) => setCal((c) => ({ ...c, academie: e.target.value }))} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="wizard-academie">
                    {cal.options.academies.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Année scolaire</Label>
                  <select value={cal.year} onChange={(e) => setCal((c) => ({ ...c, year: e.target.value }))} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="wizard-year">
                    {cal.options.years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <Button onClick={importCalendar} disabled={busy} className="mt-5 w-full rounded-xl" data-testid="wizard-calendar-import">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Importer et continuer"}
              </Button>
              <button onClick={() => setStep(6)} className="mt-3 w-full text-center text-sm text-slate-400 hover:text-slate-600" data-testid="wizard-calendar-skip">
                Configurer plus tard
              </button>
            </>
          )}

          {step === 6 && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-500/10">
                <PartyPopper size={30} />
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Votre établissement est prêt.</h1>
              <p className="text-sm text-slate-500">Vous pouvez maintenant remplir les Passeports de la semaine.</p>
              <Button onClick={async () => { try { await api.post("/auth/complete-onboarding"); await refresh(); } catch {} navigate("/enseignant"); }} className="mt-2 rounded-xl" data-testid="wizard-finish">
                Accéder à mon tableau de bord <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
